import React, { useEffect, useState } from 'react';
import {
  DataGrid,
  GridActionsCellItem,
  GridRowModes,
} from '@mui/x-data-grid';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';


import { authFetch } from './utils/api'; // ✅ Import fetch wrapperL
import { authFetchRequest } from './utils/api';
import { getFilterForUser } from './utils/utils';
import { getCookie } from './utils/cookies';
import { alertC, confirmC } from './components/dialogService';




const Inventory = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowModesModel, setRowModesModel] = useState({});
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newInventory, setNewInventory] = useState({
    PartNo: '', Description: '', DisplayName: '', Type: '', Supplier: '',
    SupplierCostList: '', SellingPrice: '', InStock: '', Available: ''
  });
  const [paginationModel, setPaginationModel] = useState({
  page: 0,
  pageSize: 10,
  });

  // Load Inventory with proper auth and error handling
useEffect(() => {
  const fetchInventory = async () => {
    setLoading(true);
    try { 
      const filter = getFilterForUser();
      const data = await authFetch(`/inventory/`);
      //alert(JSON.stringify(data, null, 2));
      
      // Validate response structure
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format');
      }
      
      setRows(data);
    } catch (err) {
      console.error('Failed to fetch Inventory:', err);
      
      // Handle specific error cases
      if (err.message.includes('401')) {
        alert('Session expired. Please login again.');
        navigate('/login');
      } else {
        alert('Failed to load Inventory. Please try again later.');
      }
      
      // Optionally set empty state
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  fetchInventory();
}, []); // Add dependencies if needed


const processRowUpdate = async (newRow) => {
  try {
      const updatedRow = await authFetchRequest(
          `/inventory/${newRow.id}`,
          newRow,
          "PUT",
          { Authorization: `Bearer ${getCookie("access_token")}` } // Example of adding a header dynamically
      );

      setRows((prev) => prev.map((row) => (row.id === newRow.id ? updatedRow : row)));

      setRowModesModel((prev) => ({
          ...prev,
          [newRow.id]: { mode: GridRowModes.View },
      }));

      return updatedRow;
  } catch (error) {
      console.error("Failed to update row:", error);
      alert(`Failed to update row: ${error.message}`);
      return newRow;
  }
};

  // Delete row
  const handleDeleteClick = (id) => async () => {
    try {
      const row = rows.find((r) => r.id === id);
      if (!row) {
        await alertC("Row not found for ID:", id);
        return;
      }
      if (parseInt(getCookie('user_id')) !== parseInt(row.fkUserId)) {
        await alertC("You don't have permission to delete this item.");
        return;
      } 
      const confirmed = await confirmC("Are you sure you want to delete this Inventory item?");
      if (!confirmed) return;
      await authFetchRequest(`/inventory/${id}`, null, "DELETE");
      setRows((prevRows) => prevRows.filter((row) => row.id !== id));
      console.log(`Inventory item ${id} deleted`);
    } catch (err) {
      console.error("Delete failed:", err);
      await alertC("Failed to delete Inventory item: " + err.message);
    }
  };
   
  // Inline editing
  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleRowEditStop = (params, event) => {
    event.defaultMuiPrevented = true;
  };



  const handleAddInventory = async () => {
    try {
      // Retrieve the user ID from cookies.
      const userIdCookie = getCookie("user_id");
      if (!userIdCookie) {
        await alertC("User ID not found in cookies!");
        return;
      }
      const fkUserId = parseInt(userIdCookie, 10);
      if (isNaN(fkUserId)) {
        await alertC("User ID is invalid!");
        return;
      }
      console.log("User ID:", fkUserId);
  
      // Prepare the payload for the new Inventory.
      // newInventory should be your local state holding the Inventory form data.
      const payload = { ...newInventory, fkUserId };
      console.log("Payload to send:", payload);
      const added =await authFetchRequest(`/inventory/`, payload);
      console.log("Inventory item successfully added:", added);
  
      // Update your local state with the new Inventory item.
      setRows((prev) => [...prev, added]);
      setNewDialogOpen(false);
      setNewInventory({
        PartNo: "",
        Description: "",
        DisplayName: "",
        Type: "0",
        Supplier: "",
        SupplierCostList: "",
        SellingPrice: "",
        InStock: "",
        Available: "",
      });
    } catch (err) {
      console.error("Failed to add Inventory item:", err);
      await alertC("Failed to add Inventory item: " + err.message);
    }
  };


  const columns = [
    { field: 'id', headerName: 'ID', width: 40, editable: false },
    { field: 'PartNo', headerName: 'Part No', width: 150, editable: true },
    { field: 'Description', headerName: 'Description', width: 200, editable: true },
    { field: 'DisplayName', headerName: 'Display Name', width: 200, editable: true },
    { field: 'Type', headerName: 'Type', width: 120, editable: true },
    { field: 'Supplier', headerName: 'Supplier', width: 100, editable: true },
    { field: 'SupplierCostList', headerName: 'Supplier Cost/List', width: 170, editable: true },
    { field: 'SellingPrice', headerName: 'Selling Price', width: 170, editable: true },
    { field: 'InStock', headerName: 'In Stock', width: 100, editable: true },
    { field: 'Available', headerName: 'Available', width: 120, editable: true },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
      
        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={() =>
                setRowModesModel((prev) => ({
                  ...prev,
                  [id]: { mode: GridRowModes.View },
                }))
              }
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              onClick={() =>
                setRowModesModel((prev) => ({
                  ...prev,
                  [id]: { mode: GridRowModes.View, ignoreModifications: true },
                }))
              }
              color="inherit"
            />,
          ];
        }
      
        return [
          <GridActionsCellItem
            icon={<Edit />}
            label="Edit"
            onClick={handleEditClick(id)}
          />,
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            onClick={handleDeleteClick(id)}
          />,
        ];
      },
      
      
    },
  ];

  return (
    <Box sx={{ width: '100%' }} className="content-container">
      {/*<Typography variant="h5" gutterBottom>Inventory</Typography>*/}
      <h3>Inventory</h3>

      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setNewDialogOpen(true)}>
          Add Inventory item
        </Button>
      </Stack>

      {loading ? (
        <CircularProgress />
      ) : (
        <DataGrid
          autoHeight
          rowHeight={30}
          rows={rows}
          columns={columns}

          pageSize={10}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => setPaginationModel(model)}
 
          rowsPerPageOptions={[10, 20, 50]} // 👈 dropdown options
          pageSizeOptions={[10, 20, 50]}    // 👈 fallback for older versions
          editMode="row"
          rowModesModel={rowModesModel}
          onRowEditStop1={handleRowEditStop}
          onRowModesModelChange={(newModel) => setRowModesModel(newModel)} // <-- Add this!
          processRowUpdate={processRowUpdate}
          onRowEditStart={(params) => {
            //console.log('Editing started:', params);
          }}
          onRowEditStop={(params, event) => {
            //console.log('Editing stopped:', params);
            event.defaultMuiPrevented = true;
          }}
          
          checkboxSelection={false}
          disableRowSelectionOnClick
          initialState={{
            sorting: {
              sortModel: [{ field: 'id', sort: 'desc' }],
            },
          }}
        />
      )}

      {/* Add Inventory Dialog */}
      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        <DialogTitle>Add New Inventory item</DialogTitle>
        <DialogContent>
          {Object.keys(newInventory).map((key) => (
            <TextField
              key={key}
              label={key}
              fullWidth
              margin="dense"
              value={newInventory[key]}
              onChange={(e) => setNewInventory({ ...newInventory, [key]: e.target.value })}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddInventory}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inventory;
